import { defineStore } from 'pinia';
import api from '@/Services/api';

export const useAuthStore = defineStore('auth', {
    state: () => {
        let cachedUser = null;
        try {
            const raw = localStorage.getItem('user');
            if (raw) cachedUser = JSON.parse(raw);
        } catch (e) {
            cachedUser = null;
        }
        return {
            user: cachedUser,
            token: localStorage.getItem('token') || null,
            isAuthenticated: !!localStorage.getItem('token'),
            loading: false,
            error: null
        };
    },

    actions: {
        async login(credentials) {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.post('/users/login', {
                    email: credentials.email,
                    password: credentials.password
                });
                this.token = response.data.access_token;
                localStorage.setItem('token', this.token);

                // Fetch user data
                const userResponse = await api.get('/users/me', {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                this.user = userResponse.data;
                localStorage.setItem('user', JSON.stringify(this.user));

                this.isAuthenticated = true;
                return true;
            } catch (err) {
                this.error = err.response?.data?.detail || 'Login failed';
                return false;
            } finally {
                this.loading = false;
            }
        },

        async initializeAuth() {
            if (!this.user) {
                try {
                    const raw = localStorage.getItem('user');
                    if (raw) this.user = JSON.parse(raw);
                } catch (e) {
                    this.user = null;
                }
            }
            if (this.token) {
                try {
                    const userResponse = await api.get('/users/me', {
                        headers: { Authorization: `Bearer ${this.token}` }
                    });
                    this.user = userResponse.data;
                    localStorage.setItem('user', JSON.stringify(this.user));
                    this.isAuthenticated = true;
                } catch (err) {
                    if (err.response?.status === 401) {
                        this.isAuthenticated = false;
                        this.user = null;
                        this.token = null;
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                    }
                }
            }
        },

        async signup(userData) {
            this.loading = true;
            this.error = null;
            try {
                // 1. Create account
                await api.post('/users/', {
                    username: userData.username,
                    email: userData.email,
                    password: userData.password
                });

                // 2. Automatically log the user in immediately so they receive their token and user data
                const loginSuccess = await this.login({
                    email: userData.email,
                    password: userData.password
                });

                return loginSuccess;
            } catch (err) {
                this.error = err.response?.data?.message || err.response?.data?.detail || 'Registration failed';
                console.error(this.error);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async googleAuth(userData) {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.post('/users/google', userData);
                const userResponseData = response.data;
                this.user = { 
                    ...userResponseData, 
                    id: userResponseData.user_id || userResponseData._id 
                };

                if (userResponseData.access_token) {
                    this.token = userResponseData.access_token;
                    localStorage.setItem('token', this.token);
                    localStorage.setItem('user', JSON.stringify(this.user));
                    this.isAuthenticated = true;
                }
                
                return true;
            } catch (err) {
                this.error = err.response?.data?.message || err.response?.data?.detail || 'Google Authentication failed';
                console.error(this.error);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async updateLocation(locationData) {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.put('/users/me/location', {
                    country: locationData.country,
                    city: locationData.city
                }, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                this.user = { ...this.user, ...response.data };
                localStorage.setItem('user', JSON.stringify(this.user));
                return true;
            } catch (err) {
                this.error = err.response?.data?.detail || 'Failed to update location';
                console.error(this.error);
                return false;
            } finally {
                this.loading = false;
            }
        },

        updateUser(partial) {
            if (this.user) {
                this.user = { ...this.user, ...partial };
                localStorage.setItem('user', JSON.stringify(this.user));
            }
        },

        logout() {
            this.user = null;
            this.token = null;
            this.isAuthenticated = false;
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        },

        clearError() {
            this.error = null;
        }
    }
});
